import React, { FC } from 'react'
import {
  Box,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { useSelect } from 'downshift'

import { Input } from '../Input'

type Option = {
  name: string
  value: string
}

export type StyledSelectProps = {
  id?: string
  isDisabled?: boolean
  onChange: (value: string | null) => void
  options: Option[]
  placeholder?: string
  value?: string | null
}

const itemToString = (item: Option | null) => (item ? item.name : '')

export const StyledSelect: FC<StyledSelectProps> = ({
  isDisabled = false,
  id,
  onChange,
  options,
  placeholder = 'Select an option',
  value,
}) => {
  const selectedItem = options.find((option) => option.value === value)

  const {
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen,
  } = useSelect({
    itemToString,
    items: options,
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      onChange(newSelectedItem ? newSelectedItem.value : null)
    },
    selectedItem,
  })

  return (
    <Box position="relative" width="100%" id={id}>
      {/* `as` is used in these elements to match style of Combobox. */}
      <InputGroup
        alignItems="stretch"
        as="button"
        width="100%"
        type="button"
        {...getToggleButtonProps({ disabled: isDisabled })}
      >
        <Input
          as="span"
          display="inline-flex"
          alignItems="center"
          paddingY="xxs"
        >
          <Box
            as="span"
            {
              // Manually set placeholder color. Not automatic because item is rendered as span.
              ...(!selectedItem && { color: 'softestBlack' })
            }
          >
            {selectedItem ? itemToString(selectedItem) : placeholder}
          </Box>
        </Input>
        <InputRightElement
          pointerEvents="none"
          top="50%"
          transform="translateY(-50%)"
        >
          {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </InputRightElement>
      </InputGroup>
      <List
        boxShadow="md"
        maxHeight="20ex"
        overflowY="scroll"
        position="absolute"
        width="100%"
        zIndex="dropdown"
        {...(!isOpen && { display: 'none' })}
        {...getMenuProps()}
      >
        {isOpen &&
          options.map((option, index) => (
            <ListItem
              key={option.value}
              layerStyle="softestWhite"
              px="lg"
              py="sm"
              {...(highlightedIndex === index && {
                layerStyle: 'softerOrange',
              })}
              {...(selectedItem === option && { layerStyle: 'softBlack' })}
              {...getItemProps({ item: option, index })}
            >
              {option.name}
            </ListItem>
          ))}
      </List>
    </Box>
  )
}
